import { useState, useEffect, useRef, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import axios from "axios";
import LoginPage from "@/pages/LoginPage";
import ClaimsList from "@/pages/ClaimsList";
import ClaimDetail from "@/pages/ClaimDetail";
import FraudDashboard from "@/pages/FraudDashboard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Set default auth header from stored token
const storedToken = localStorage.getItem("token");
if (storedToken) {
  axios.defaults.headers.common["Authorization"] = `Bearer ${storedToken}`;
}

// Global 401 interceptor
axios.interceptors.response.use(
  res => res,
  err => {
    if (err?.response?.status === 401 && window.location.pathname !== "/") {
      localStorage.removeItem("token");
      localStorage.removeItem("user_name");
      localStorage.removeItem("user_email");
      delete axios.defaults.headers.common["Authorization"];
      window.location.href = "/";
    }
    return Promise.reject(err);
  }
);

function App() {
  const [authed, setAuthed] = useState(!!localStorage.getItem("token"));
  const [checking, setChecking] = useState(true);
  const apiRef = useRef(API);

  useEffect(() => {
    const t = localStorage.getItem("token");
    if (!t) { setChecking(false); return; }
    axios.get(`${apiRef.current}/auth/me`, { headers: { Authorization: `Bearer ${t}` } })
      .then(() => { setAuthed(true); })
      .catch(() => {
        localStorage.removeItem("token");
        localStorage.removeItem("user_name");
        localStorage.removeItem("user_email");
        delete axios.defaults.headers.common["Authorization"];
        setAuthed(false);
      })
      .finally(() => setChecking(false));
  }, []);

  const handleLogin = useCallback((data) => {
    axios.defaults.headers.common["Authorization"] = `Bearer ${data.token}`;
    setAuthed(true);
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("user_name");
    localStorage.removeItem("user_email");
    delete axios.defaults.headers.common["Authorization"];
    setAuthed(false);
  }, []);

  if (checking) {
    return <div className="h-screen flex items-center justify-center bg-gray-50">
      <div className="w-6 h-6 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin" />
    </div>;
  }

  if (!authed) {
    return (
      <>
        <LoginPage onLogin={handleLogin} />
        <Toaster position="top-right" richColors />
      </>
    );
  }

  return (
    <div className="h-screen overflow-hidden">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<ClaimsList onLogout={handleLogout} />} />
          <Route path="/claim/:claimId" element={<ClaimDetail />} />
          <Route path="/fraud" element={<FraudDashboard />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
